import { StandardEvents } from '@shopify/events';

const RECOMMENDATION_LIMIT = 10;
const VISIBLE_LIMIT = 12;

class CartRecommendations extends HTMLElement {
  static observedAttributes = ['data-product-id', 'data-cart-product-ids'];

  /** @type {AbortController | null} */
  #abortController = null;

  #loadScheduled = false;

  connectedCallback() {
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    this.#scheduleLoad();
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    this.#abortController?.abort();
  }

  attributeChangedCallback(_name, previousValue, nextValue) {
    if (!this.isConnected || previousValue === nextValue) return;
    this.#scheduleLoad();
  }

  #handleCartUpdate = (event) => {
    this.#abortController?.abort();
    this.#hide();
    event.promise
      ?.then(() => {
        requestAnimationFrame(() => requestAnimationFrame(() => this.#scheduleLoad()));
      })
      .catch(() => {
        this.#hide();
      });
  };

  #scheduleLoad() {
    if (this.#loadScheduled) return;

    this.#loadScheduled = true;
    queueMicrotask(() => {
      this.#loadScheduled = false;
      if (this.isConnected) this.#load();
    });
  }

  async #load() {
    const productId = this.dataset.productId?.trim();
    const endpoint = this.#sameOriginUrl(this.dataset.endpoint);
    const list = this.querySelector('[data-cart-recommendations-list]');

    if (!productId || !endpoint || !(list instanceof HTMLElement)) {
      this.#hide();
      return;
    }

    this.#abortController?.abort();
    const abortController = new AbortController();
    this.#abortController = abortController;
    this.hidden = true;
    this.setAttribute('aria-busy', 'true');
    list.replaceChildren();

    endpoint.searchParams.set('product_id', productId);
    endpoint.searchParams.set('limit', String(RECOMMENDATION_LIMIT));
    endpoint.searchParams.set('intent', 'related');

    try {
      // Shopify renders these IDs with each native cart section update.
      // Do not gate server-rendered candidates on an extra cart API request.
      const cartProductIds = new Set((this.dataset.cartProductIds || '').split(',').map(id => id.trim()).filter(Boolean));
      if (!cartProductIds.size) { this.#hide(); return; }
      let collectionProducts = [];
      try { collectionProducts = JSON.parse(this.querySelector('[data-cart-collection-candidates]')?.textContent || '[]'); } catch { /* Related API remains available. */ }
      if (!Array.isArray(collectionProducts)) collectionProducts = [];
      const eligibleIds = new Set(collectionProducts.filter(p => this.#normalizeProduct(p, cartProductIds)).map(p => String(p.id)));
      let payload = {};
      if (eligibleIds.size < VISIBLE_LIMIT) {
        try {
          const response = await fetch(endpoint, { headers: { Accept: 'application/json' }, signal: abortController.signal });
          if (response.ok) payload = await response.json();
        } catch (error) {
          if (error?.name === 'AbortError') return;
          // Retain verified collection candidates when the supplemental API is unavailable.
        }
      }
      if (this.#abortController !== abortController) return;
      const products = [...collectionProducts, ...(Array.isArray(payload?.products) ? payload.products : [])];
      const seenProductIds = new Set(cartProductIds);
      const recommendations = [];

      for (const product of products) {
        const recommendation = this.#normalizeProduct(product, seenProductIds);
        if (!recommendation) continue;

        seenProductIds.add(recommendation.id);
        recommendations.push(recommendation);
        if (recommendations.length === VISIBLE_LIMIT) break;
      }

      if (recommendations.length === 0) {
        this.#hide();
        return;
      }

      const fragment = document.createDocumentFragment();
      for (const recommendation of recommendations) {
        fragment.append(this.#createCard(recommendation));
      }

      list.replaceChildren(fragment);
      this.removeAttribute('aria-busy');
      this.hidden = false;
    } catch (error) {
      if (error?.name !== 'AbortError' && this.#abortController === abortController) this.#hide();
    } finally {
      if (this.#abortController === abortController) this.#abortController = null;
    }
  }

  #hide() {
    this.hidden = true;
    this.removeAttribute('aria-busy');
    this.querySelector('[data-cart-recommendations-list]')?.replaceChildren();
  }

  #normalizeProduct(product, excludedIds) {
    if (!product || typeof product !== 'object' || product.available !== true) return null;

    const id = String(product.id || '').trim();
    const title = String(product.title || '').trim();
    const url = this.#productUrl(product);
    const imageUrl = this.#imageUrl(product);
    const rawPrice = Number(product.price_min ?? product.price);

    if (!id || excludedIds.has(id) || !title || !url || !imageUrl || !Number.isFinite(rawPrice) || rawPrice < 0) {
      return null;
    }

    const variants = Array.isArray(product.variants) ? product.variants : [];
    const options = Array.isArray(product.options) ? product.options : [];

    return {
      id,
      title,
      url,
      imageUrl,
      price: this.#formatMoney(rawPrice),
      priceVaries: product.price_varies === true,
      optionsCount: options.length,
      requiresDeliberateSelection: (product.variants_count ?? variants.length) !== 1,
      usesSellingPlans: product.requires_selling_plan === true,
    };
  }

  #productUrl(product) {
    const rootUrl = this.dataset.rootUrl || '/';
    const handle = String(product.handle || '').trim();
    const candidate = product.url || (handle ? `${rootUrl}products/${encodeURIComponent(handle)}` : '');
    const url = this.#sameOriginUrl(candidate);

    return url ? `${url.pathname}${url.search}` : null;
  }

  #imageUrl(product) {
    const featuredImage =
      typeof product.featured_image === 'string'
        ? product.featured_image
        : product.featured_image?.src || product.featured_image?.url;
    const firstImage = Array.isArray(product.images) ? product.images[0] : null;
    const candidate = featuredImage || (typeof firstImage === 'string' ? firstImage : firstImage?.src || firstImage?.url);

    if (!candidate) return null;

    try {
      const url = new URL(candidate, window.location.origin);
      if (!['http:', 'https:'].includes(url.protocol)) return null;
      return url.href;
    } catch {
      return null;
    }
  }

  #sameOriginUrl(candidate) {
    if (!candidate) return null;

    try {
      const url = new URL(candidate, window.location.origin);
      return url.origin === window.location.origin ? url : null;
    } catch {
      return null;
    }
  }

  #sizedImageUrl(candidate, width) {
    try {
      const url = new URL(candidate, window.location.origin);
      if (!['http:', 'https:'].includes(url.protocol)) return candidate;

      if (url.hostname === 'cdn.shopify.com' || url.hostname.endsWith('.myshopify.com')) {
        url.searchParams.set('width', String(width));
      }

      return url.href;
    } catch {
      return candidate;
    }
  }

  #formatMoney(priceInMinorUnits) {
    const locale = this.dataset.locale || document.documentElement.lang || 'en-US';
    const currency = this.dataset.currency || window.Shopify?.currency?.active || 'USD';

    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(priceInMinorUnits / 100);
    } catch {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
        priceInMinorUnits / 100
      );
    }
  }

  #createCard(product) {
    const card = document.createElement('article');
    card.dataset.productId = product.id;
    card.className = 'cart-recommendations__card';
    card.setAttribute('role', 'listitem');

    const mediaLink = document.createElement('a');
    mediaLink.className = 'cart-recommendations__media';
    mediaLink.href = product.url;

    const image = document.createElement('img');
    image.className = 'cart-recommendations__image';
    image.src = this.#sizedImageUrl(product.imageUrl, 240);
    image.srcset = [160, 240, 320]
      .map((width) => `${this.#sizedImageUrl(product.imageUrl, width)} ${width}w`)
      .join(', ');
    image.sizes = '112px';
    image.alt = product.title;
    image.loading = 'eager';
    image.fetchPriority = 'low';
    image.decoding = 'async';
    image.width = 360;
    image.height = 450;
    mediaLink.append(image);

    const content = document.createElement('div');
    content.className = 'cart-recommendations__content';

    const title = document.createElement('a');
    title.className = 'cart-recommendations__title';
    title.href = product.url;
    title.textContent = product.title;

    const price = document.createElement('p');
    price.className = 'cart-recommendations__price';
    price.textContent = `${product.priceVaries ? 'From ' : ''}${product.price}`;
    content.append(title, price);

    const quickAdd = document.createElement('quick-add-component');
    // Do not apply Horizon's product-card `.quick-add` positioning class here:
    // in the cart this component is an in-flow action, not an image overlay.
    quickAdd.className = 'cart-recommendations__quick-add';
    quickAdd.dataset.productUrl = product.url;
    quickAdd.dataset.productTitle = product.title;
    quickAdd.dataset.productId = product.id;
    quickAdd.dataset.quickAddButton = 'choose';
    quickAdd.dataset.productOptionsCount = String(product.optionsCount);
    quickAdd.dataset.usesSellingPlans = String(product.usesSellingPlans);
    quickAdd.dataset.requiresDeliberateSelection = String(product.requiresDeliberateSelection);
    quickAdd.dataset.rendersBothButtons = 'false';

    const button = document.createElement('button');
    button.className = 'button quick-add__button quick-add__button--choose cart-recommendations__button';
    button.type = 'button';
    button.name = 'add';
    button.setAttribute('on:click', 'quick-add-component/handleClick');
    button.setAttribute('aria-haspopup', 'dialog');
    button.setAttribute('aria-controls', 'quick-add-dialog');
    button.setAttribute('aria-label', `Quick add: ${product.title}`);
    button.textContent = '+ Add';
    quickAdd.append(button);

    card.append(mediaLink, content, quickAdd);
    return card;
  }
}

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}
